import { Paper, Tab, Tabs, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Maps from "./Maps/Maps";
import CalculationTypes from "./CalculationTypes/CalculationTypes";
import UniqueDefinitions from "./UniqueDefinitions/UniqueDefinitions";
import { selectUserData } from "../../../features/userSlice";
import { CheckPermissions } from "../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../Page404/Page403";
import "./Unifier.css";

// the tab index is kept in the URL so map/definition sub-pages can send the
// user back to the tab they came from
export const UNIFIER_TAB = {
  MAPS: 0,
  CALCULATION_TYPES: 1,
  UNIQUE_DEFINITIONS: 2,
};

const Unifier = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);

  const hasViewMapsPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "unifier",
    "view_maps",
  );
  const hasViewCalculationTypesPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "unifier",
    "view_calculation_types",
  );
  const hasViewUniqueDefinitionsPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "unifier",
    "view_unique_definitions",
  );

  const unifierTabs = [
    {
      id: UNIFIER_TAB.MAPS,
      label: "Maps",
      disabled: !hasViewMapsPermission,
      tracker: "1074",
    },
    {
      id: UNIFIER_TAB.CALCULATION_TYPES,
      label: "Calculation Types",
      disabled: !hasViewCalculationTypesPermission,
      tracker: "1075",
    },
    {
      id: UNIFIER_TAB.UNIQUE_DEFINITIONS,
      label: "Unique Definitions",
      disabled: !hasViewUniqueDefinitionsPermission,
      tracker: "1076",
    },
  ];

  const firstEnabledTab = unifierTabs.find((tab) => !tab.disabled);

  const tabFromUrl = parseInt(searchParams.get("tab") ?? "", 10);
  // a disabled tab is never selectable, so a stale ?tab=1 falls back to the
  // first tab the user is allowed to see
  const selectedTab = unifierTabs.some(
    (tab) => tab.id === tabFromUrl && !tab.disabled,
  )
    ? tabFromUrl
    : (firstEnabledTab?.id ?? UNIFIER_TAB.MAPS);

  const handleChangeTab = (_: unknown, newValue: number) => {
    // each tab brings its own search, filters and pagination params, so the
    // query string is rebuilt from scratch rather than carried across
    setSearchParams(
      () => new URLSearchParams({ tab: String(newValue) }),
      { replace: true },
    );
  };

  // no view permission on any of the three sections means no module at all
  if (!firstEnabledTab) {
    return <Page404 />;
  }

  return (
    <Paper elevation={0} sx={{ padding: "14px 16px" }}>
      <Typography variant="h3" sx={{ fontSize: "24px", mb: 1.5 }}>
        Unifier
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mx: "-16px" }}>
        <Tabs
          className="unifierTabs"
          sx={{ pr: "16px" }}
          value={selectedTab}
          onChange={handleChangeTab}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="Unifier tabs"
        >
          {unifierTabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              disabled={tab.disabled}
              data-tracker={tab.tracker}
              id={`unifier-tab-${tab.id}`}
              aria-controls={`unifier-tabpanel-${tab.id}`}
            />
          ))}
        </Tabs>
      </Box>

      <div
        role="tabpanel"
        hidden={selectedTab !== UNIFIER_TAB.MAPS}
        id={`unifier-tabpanel-${UNIFIER_TAB.MAPS}`}
        aria-labelledby={`unifier-tab-${UNIFIER_TAB.MAPS}`}
      >
        {selectedTab === UNIFIER_TAB.MAPS && <Maps />}
      </div>

      <div
        role="tabpanel"
        hidden={selectedTab !== UNIFIER_TAB.CALCULATION_TYPES}
        id={`unifier-tabpanel-${UNIFIER_TAB.CALCULATION_TYPES}`}
        aria-labelledby={`unifier-tab-${UNIFIER_TAB.CALCULATION_TYPES}`}
      >
        {selectedTab === UNIFIER_TAB.CALCULATION_TYPES && <CalculationTypes />}
      </div>

      <div
        role="tabpanel"
        hidden={selectedTab !== UNIFIER_TAB.UNIQUE_DEFINITIONS}
        id={`unifier-tabpanel-${UNIFIER_TAB.UNIQUE_DEFINITIONS}`}
        aria-labelledby={`unifier-tab-${UNIFIER_TAB.UNIQUE_DEFINITIONS}`}
      >
        {selectedTab === UNIFIER_TAB.UNIQUE_DEFINITIONS && <UniqueDefinitions />}
      </div>
    </Paper>
  );
};

export default Unifier;
